import { zodResolver } from "@hookform/resolvers/zod"
import { Box } from "@mui/material"
import type { FC } from "react"
import { useEffect, useMemo } from "react"
import type { UseFormReturn } from "react-hook-form"
import { FormProvider, useForm, useWatch } from "react-hook-form"

import type { SalesProduct } from "../../../types"

import { DEFAULT_LIST_PRICING_VALUES, buildSpecialPricingDefaults } from "./constants"
import { ListPriceFields } from "./ListPriceFields"
import { PackagingDisplay } from "./PackagingDisplay"
import { PricingPathRadio } from "./PricingPathRadio"
import { type PricingFormValues, pricingFormSchema } from "./schemas"
import { SpecialPriceFields } from "./SpecialPriceFields"
import { PricingPath } from "./types"
import { toPricingTierFormValues } from "./utils"

import { type Incoterms, type PriceChangeItem } from "@/graphql/codegen/graphql"
import { useLookupProductWarehouses } from "@/hooks/useLookupProductWarehouses"
import { mapPriceChangeLookupItemsToOptions } from "@/screens/Pricing/utils/lookup"

const resolveShipFromAsArray = (
  shipFromLocationIds: string[] | undefined,
  shipFromLocationId: string | undefined
): string[] => {
  if (shipFromLocationIds && shipFromLocationIds.length > 0) return shipFromLocationIds
  if (shipFromLocationId) return [shipFromLocationId]
  return []
}

interface CarriedOverPathValues {
  shipFromAsArray: string[]
  incotermsAsArray: Incoterms[]
  incotermsLocationId: string
  estimatedAnnualVolume: number | null
}

// Pulls the path-agnostic values out of the current form state so handlePathChange can
// re-seed the next path's defaults without losing user input. Carry-over is asymmetric:
// List -> Special promotes the single incoterm into a one-element array (lossless), but
// Special -> List hard-resets incoterms + incotermsLocationId in handlePathChange to avoid
// silently dropping the extra selections.
const buildCarriedOverPathValues = (currentValues: PricingFormValues): CarriedOverPathValues => {
  const previousShipFromArray = "shipFromLocationIds" in currentValues ? currentValues.shipFromLocationIds : undefined
  const previousShipFromSingle = "shipFromLocationId" in currentValues ? currentValues.shipFromLocationId : undefined
  const previousIncoterms = "incoterms" in currentValues ? currentValues.incoterms : undefined
  const incotermsAsArray: Incoterms[] = Array.isArray(previousIncoterms)
    ? previousIncoterms
    : previousIncoterms
      ? [previousIncoterms as Incoterms]
      : []
  return {
    shipFromAsArray: resolveShipFromAsArray(previousShipFromArray, previousShipFromSingle),
    incotermsAsArray,
    incotermsLocationId: currentValues.incotermsLocationId ?? "",
    estimatedAnnualVolume: currentValues.estimatedAnnualVolume ?? null,
  }
}

interface ProductPricingFormProps {
  product: SalesProduct
  customerId: string
  initialValues?: PricingFormValues
  onRegisterMethods: (productId: number, methods: UseFormReturn<PricingFormValues>) => void
  onUnregisterMethods: (productId: number) => void
  onPathChange?: (path: PricingPath) => void
  onMissingListPriceChange?: (isMissing: boolean) => void
}

export const ProductPricingForm: FC<ProductPricingFormProps> = ({
  product,
  customerId,
  initialValues,
  onRegisterMethods,
  onUnregisterMethods,
  onPathChange,
  onMissingListPriceChange,
}) => {
  const methods = useForm<PricingFormValues>({
    resolver: zodResolver(pricingFormSchema),
    defaultValues: initialValues ?? DEFAULT_LIST_PRICING_VALUES,
    mode: "onChange",
  })

  useEffect(() => {
    onRegisterMethods(product.productId, methods)
    return () => onUnregisterMethods(product.productId)
  }, [product.productId, methods, onRegisterMethods, onUnregisterMethods])

  const path = useWatch({ control: methods.control, name: "path" })

  useEffect(() => {
    onPathChange?.(path)
  }, [path, onPathChange])

  const { warehouses } = useLookupProductWarehouses({ productId: product.productId })
  const shipFromOptions = useMemo(() => mapPriceChangeLookupItemsToOptions(warehouses), [warehouses])

  const handlePathChange = (nextPath: PricingPath) => {
    // FormRadioGroup updates the form's `path` field BEFORE this callback runs,
    // so the current values are still the previous-path shape.
    const carried = buildCarriedOverPathValues(methods.getValues())

    if (nextPath === PricingPath.Special) {
      methods.reset({
        ...buildSpecialPricingDefaults(),
        estimatedAnnualVolume: carried.estimatedAnnualVolume,
        shipFromLocationIds: carried.shipFromAsArray,
        incoterms: carried.incotermsAsArray,
        incotermsLocationId: carried.incotermsLocationId,
      })
      return
    }

    methods.reset({
      ...DEFAULT_LIST_PRICING_VALUES,
      estimatedAnnualVolume: carried.estimatedAnnualVolume,
      shipFromLocationId: carried.shipFromAsArray[0] ?? "",
    })
  }

  // Switching to Special via "Add" on an existing-specials block: the clicked special is the
  // user's intent, so its tiers/values override anything they had typed on the List form.
  // The existing special was narrowed by the user's Incoterms / Incoterms Location selection,
  // so carry those over.
  const handleSwitchToSpecial = (special: PriceChangeItem) => {
    const carried = buildCarriedOverPathValues(methods.getValues())

    methods.reset({
      ...buildSpecialPricingDefaults(),
      estimatedAnnualVolume: carried.estimatedAnnualVolume,
      shipFromLocationIds: carried.shipFromAsArray,
      incoterms: carried.incotermsAsArray,
      incotermsLocationId: carried.incotermsLocationId,
      freightTerms: special.freightTerms ?? undefined,
      tiers: toPricingTierFormValues(special.tiers),
    })
  }

  return (
    <FormProvider {...methods}>
      <Box className='flex flex-col gap-4 pb-4'>
        <PricingPathRadio productId={product.productId} onChange={handlePathChange} />
        {path === PricingPath.List && (
          <ListPriceFields
            productId={product.productId}
            customerId={customerId}
            shipFromOptions={shipFromOptions}
            productLocationsAssociations={product.locationsAssociations ?? []}
            onSwitchToSpecial={handleSwitchToSpecial}
            onMissingListPriceChange={onMissingListPriceChange}
          />
        )}
        {path === PricingPath.Special && (
          <SpecialPriceFields
            productId={product.productId}
            shipFromOptions={shipFromOptions}
            productLocationsAssociations={product.locationsAssociations ?? []}
          />
        )}
        <PackagingDisplay product={product} />
      </Box>
    </FormProvider>
  )
}
